/**
 * Shared hierarchy builder for the Freshdesk Solutions tree.
 *
 * Freshdesk folders are sub-categories, so this flattens the two levels into a
 * single top-down ordered list (a category, then its folders, then any orphan
 * folders) that both the `CategoryFolderSelect` dropdown and the `SolutionsBrowser`
 * left pane render. Purely data - callers own the (optionally market-scoped) inputs
 * and the selection.
 */
import type {
  FreshdeskSolutionCategory,
  FreshdeskSolutionFolder,
} from '../../../services/admin/integrationsApi'

export interface SolutionsTreeNode {
  type: 'category' | 'folder'
  id: number
  /** Display label (category / folder name). */
  label: string
  /** Parent category name (folders only) - drives the breadcrumb. */
  category?: string
  /** Text matched while filtering (folders include their category). */
  searchText: string
}

/**
 * Flat, top-down node list: each category followed by its (alphabetical) folders,
 * then orphan folders last. `mode='category'` lists categories only.
 */
export const buildSolutionsTree = (
  categories: FreshdeskSolutionCategory[],
  folders: FreshdeskSolutionFolder[],
  market?: 'ANZ' | 'USA' | null,
  mode: 'both' | 'category' = 'both',
): SolutionsTreeNode[] => {
  const cats = (market ? categories.filter((c) => c.market === market) : categories)
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name))
  const foldersByCat = new Map<number, FreshdeskSolutionFolder[]>()
  const orphanFolders: FreshdeskSolutionFolder[] = []
  for (const f of market ? folders.filter((f) => f.market === market) : folders) {
    if (f.category_id == null) {
      orphanFolders.push(f)
      continue
    }
    const list = foldersByCat.get(f.category_id) ?? []
    list.push(f)
    foldersByCat.set(f.category_id, list)
  }

  const nodes: SolutionsTreeNode[] = []
  for (const c of cats) {
    nodes.push({
      type: 'category',
      id: c.category_id,
      label: c.name,
      searchText: c.name,
    })
    if (mode === 'both') {
      const kids = (foldersByCat.get(c.category_id) ?? [])
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))
      for (const f of kids) {
        nodes.push({
          type: 'folder',
          id: f.folder_id,
          label: f.name,
          category: c.name,
          searchText: `${c.name} ${f.name}`,
        })
      }
    }
  }
  if (mode === 'both' && orphanFolders.length) {
    for (const f of orphanFolders.slice().sort((a, b) => a.name.localeCompare(b.name))) {
      nodes.push({
        type: 'folder',
        id: f.folder_id,
        label: f.name,
        searchText: f.name,
      })
    }
  }
  return nodes
}
